// import * as Sentry from '@sentry/react';

/**
 * Log an error to Sentry and optionally to the console in development
 * @param message - Error message
 * @param error - Error object or additional context
 * @param context - Additional context for the error
 */
export function logError(message: string, error?: unknown, context?: Record<string, any>): void {
  // In development, we might want to see errors in console
  if (import.meta.env.DEV) {
    console.error(message, error);
  }

  // Send to Sentry - disabled
  // if (error instanceof Error) {
  //   Sentry.captureException(error, {
  //     tags: {
  //       errorMessage: message
  //     },
  //     extra: context
  //   });
  // } else {
  //   Sentry.captureMessage(message, 'error', {
  //     extra: {
  //       error,
  //       ...context
  //     }
  //   });
  // }
}

/**
 * Handle errors with user feedback
 * @param error - Error object
 * @param userMessage - Message to show to the user
 * @param showToast - Toast function to display error
 */
export function handleError(
  error: unknown,
  userMessage: string,
  showToast?: (options: { title: string; description: string; variant: string }) => void
): void {
  // Log to Sentry
  logError(userMessage, error);

  // Show user feedback if toast is provided
  if (showToast) {
    showToast({
      title: 'Error',
      description: userMessage,
      variant: 'destructive',
    });
  }
}

/**
 * Wrap an async function with error handling
 * @param fn - Async function to wrap
 * @param errorMessage - Error message to display
 * @param showToast - Toast function for user feedback
 */
export function withErrorHandling<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  errorMessage: string,
  showToast?: (options: { title: string; description: string; variant: string }) => void
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, errorMessage, showToast);
      throw error;
    }
  }) as T;
}

/**
 * Safe JSON parse with error handling
 * @param json - JSON string to parse
 * @param fallback - Fallback value if parsing fails
 */
export function safeJsonParse<T = any>(json: string, fallback: T): T {
  try {
    return JSON.parse(json);
  } catch (error) {
    logError('Failed to parse JSON', error, { json });
    return fallback;
  }
}

/**
 * Create a typed error class
 */
export class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public context?: Record<string, any>
  ) {
    super(message);
    this.name = 'AppError';

    // Capture to Sentry immediately - disabled
    // Sentry.captureException(this, {
    //   tags: {
    //     errorCode: code,
    //     statusCode: statusCode?.toString()
    //   },
    //   extra: context
    // });
  }
}
